import React from 'react';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import { HChapter, HMetaphor, HSection, HParagraph, HTip, HBold } from '../HandbookContent';

export default function ChapterAnalytics() {
  return (
    <HChapter
      title="Analytics & Diagnostics"
      subtitle="Understand how your playbooks are performing and identify areas for improvement."
    >
      <HMetaphor
        icon={<ChartBarIcon className="w-5 h-5 text-lex-accent" />}
        title="The Dashboard"
      >
        Think of analytics as the dashboard in your car. It tells you how fast you're going,
        how much fuel is left, and whether the engine light is on. The EdgePlaybook dashboard shows
        you how your workflows are performing — how quickly cases move through each stage, where
        bottlenecks occur, and whether your team is meeting SLA targets.
      </HMetaphor>

      <HSection title="Key Metrics">
        <HParagraph>
          The Analytics tab in the playbook detail view shows four headline metrics:
        </HParagraph>
        <img
          src="/handbook/analytics-dashboard.svg"
          alt="Analytics dashboard showing date range buttons (7d active, 30d, 90d), four metric cards (42 Total Instances, 78% Completion Rate in amber, 36h Avg Completion, 91% SLA Compliance in emerald), a Completion Funnel with bars for Started/In Progress/Completed/Stuck, and Trigger Distribution showing Manual 60%, Event 25%, Deadline 15%"
          className="rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm mb-4 w-full"
        />
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <div className="text-sm font-medium text-gray-900 dark:text-white">Total Instances</div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">How many matters/tasks are using this playbook</p>
          </div>
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <div className="text-sm font-medium text-gray-900 dark:text-white">Completion Rate</div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Percentage of instances that reached a terminal status</p>
          </div>
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <div className="text-sm font-medium text-gray-900 dark:text-white">Avg. Completion Time</div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Average hours from start to finish</p>
          </div>
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <div className="text-sm font-medium text-gray-900 dark:text-white">SLA Compliance</div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Percentage of statuses meeting their time targets</p>
          </div>
        </div>
      </HSection>

      <HSection title="Completion Funnel">
        <HParagraph>
          The funnel chart shows how matters flow through the playbook: how many started, how many
          are in progress, how many completed, and how many are stuck. A healthy funnel narrows
          gradually. If "Stuck" is high, investigate which statuses are causing delays.
        </HParagraph>
      </HSection>

      <HSection title="Bottleneck Detection">
        <HParagraph>
          The <HBold>Status Dwell Times</HBold> chart shows how long matters spend at each status,
          on average. Statuses where matters linger the longest are highlighted in red — these are
          your bottlenecks. Look at the p95 (95th percentile) time to understand worst-case scenarios.
        </HParagraph>
      </HSection>

      <HSection title="Guard Failure Rates">
        <HParagraph>
          This table shows which guards are failing most often and on which transitions. A high failure
          rate might mean the guard is too strict, or that your team needs better guidance on
          prerequisites before attempting a transition.
        </HParagraph>
      </HSection>

      <HSection title="Version Comparison">
        <HParagraph>
          If your playbook has multiple published versions, the <HBold>Version Compare</HBold> view
          shows side-by-side performance metrics for each version. This helps you determine whether
          workflow changes actually improved outcomes.
        </HParagraph>
      </HSection>

      <HSection title="Date Range Filtering">
        <HParagraph>
          Use the date range buttons (7d, 30d, 90d) at the top of the Analytics tab to focus on
          recent data or look at longer trends.
        </HParagraph>
      </HSection>

      <HTip>
        Check analytics weekly. If a status consistently has high dwell times, consider whether
        the guards on its outgoing transitions are too restrictive, or whether your team needs
        more resources at that stage.
      </HTip>
    </HChapter>
  );
}
